import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {dispatch, useSelector} from "../../redux/store";
import LoadingScreen from "../../components/loading-screen";
import {useSnackbar} from "notistack";
import {useLocales} from "../../locales";
import DepartureCard from "./DepartureCard";
import {setDefaultUserSettings} from "../../redux/slices/userSettings";
import {Stack, Typography} from "@mui/material";
import {getSessionSelectedLocation, removeSessionOperator, setSessionSelectedLocation} from "../../helper/session";
import {PATH_AUTH} from "../../routes/path";
import {fetchAllLocations} from "../../redux/slices/locations";
import LocationDialog from "../../components/LocationDialog";
import EmptyContent from "../../components/empty-content";
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import {getStationScheduler} from "../../api/currentTripSchedule";

const MAX_ROWS = 6;
const MIN_ROWS = 15;

export default function DeparturePreview() {
    const {enqueueSnackbar} = useSnackbar();
    const {translate} = useLocales();

    const [loading, setLoading] = useState(false);
    const [trips, setTrips] = useState([]);
    const [page, setPage] = useState(1);
    const [fetchingMore, setFetchingMore] = useState(null);
    const [openLocationDialog, setOpenLocationDialog] = useState(false);
    const [currentDateTime, setCurrentDateTime] = useState(dayjs());

    const timerRef = useRef(null);
    const abortRef = useRef(null);

    const {locations, loading: loadingLocation, error} = useSelector(select => select.locations);
    const {defaultDepStations, loading: loadingUserSettings, services} = useSelector(state => state.userSettings);

    // Prekini zahtev u toku kada se komponenta demontira (npr. odjava),
    // da odgovor koji kasni ne bi gazio stanje novog mount-a.
    useEffect(() => () => abortRef.current?.abort(), []);

    const nextSignal = () => {
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        return abortRef.current.signal;
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentDateTime(dayjs());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setPage(prevState => prevState === 2 ? 1 : 2)
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setTrips(prevState => prevState.filter(trip =>
                moment().isBefore(trip.fromStationDepartureDt)))
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    // Učitaj raspored za trenutnu polaznu stanicu i ponovo učitaj kada se ona promeni.
    // Ključ je sam ID stanice (primitiv), a ne `trips.length`: posle ponovne prijave
    // u memoriji ostaje keširan `defaultDepStations`, pa bi stara vrednost mogla da
    // ostavi listu praznom ili da prikaže pogrešnu stanicu. Vezivanje za ID osigurava
    // da se uvek povuče tačna stanica čim se njena vrednost ustali.
    useEffect(() => {
        if (defaultDepStations?.[0]) {
            fetchInitData(defaultDepStations[0]);
        }
        // eslint-disable-next-line
    }, [defaultDepStations?.[0]]);

    useEffect(() => {
        dispatch(setDefaultUserSettings());
    }, []);

    useEffect(() => {
        // Tek nakon što je inicijalno učitavanje popunilo listu dopunjavamo redove.
        // Bez `trips.length > 0` ovaj efekat bi se okidao na praznom početnom stanju
        // i paralelno sa fetchInitData (koji još nije commit-ovao loading),
        // pa bi dva zahteva prekidala jedan drugog i lista bi povremeno ostala prazna.
        if (!fetchingMore && !loading && trips.length > 0 && trips.length < MIN_ROWS) {
            fetchMoreData(defaultDepStations?.[0]);
        }
    }, [trips.length]);

    useLayoutEffect(() => {
        dispatch(fetchAllLocations()).unwrap().then(response => {
            if (response.length === 1) {
                setSessionSelectedLocation(response[0]);
            } else {
                if (getSessionSelectedLocation() === undefined) {
                    setOpenLocationDialog(true);
                } else {
                    let selected = response.some(location => location.PAC === getSessionSelectedLocation().PAC);
                    if (!selected) {
                        setOpenLocationDialog(true);
                    }
                }
            }
        }).catch(reason => {
            console.error("fetchAllLocations", reason);
            enqueueSnackbar("Nije moguće očitati lokacije!", {variant: "error"});
        });
        // eslint-disable-next-line
    }, []);

    const fetchInitData = (depId) => {
        if (!depId) {
            return;
        }
        setLoading(true);
        getStationScheduler(depId, nextSignal()).then(({data}) => {
            if (data) {
                setTrips(data);
            }
        }).catch(reason => {
            if (reason?.code === "ERR_CANCELED") {
                return;
            }
            enqueueSnackbar(translate("errorFetching"), {variant: "error"});
            console.error("fetchInitData", reason);
        }).finally(() => {
            setLoading(false);
        });
    };

    const fetchMoreData = (depId) => {
        if (!depId || loading) {
            return;
        }
        setFetchingMore(true)
        // Dopuna koristi signal tekućeg ciklusa (ne pravi novi), da ne bi prekinula
        // inicijalni zahtev; demontaža i dalje otkazuje oba preko istog kontrolera.
        getStationScheduler(depId, abortRef.current?.signal).then(({data}) => {
            if (data) {
                setTrips([...data, ...data]);
            }
        }).catch(reason => {
            if (reason?.code === "ERR_CANCELED") {
                return;
            }
            console.error("fetchMoreData", reason);
        }).finally(() => {
            setFetchingMore(false)
        })
    }

    const handleLogout = (event) => {
        if (event.detail > 3) {
            try {
                removeSessionOperator();
                clearInterval(timerRef.current);
                // Pun reload (a ne SPA navigacija): odjava operatera ne radi reload,
                // pa bi staro stanje u memoriji (redux store) preživelo i sledeća
                // prijava bi prikazala praznu listu. `window.location` osigurava čist
                // start identičan ručnom F5 koji problem rešava.
                window.location.assign(PATH_AUTH.login);
            } catch (error) {
                console.error(error);
                enqueueSnackbar('Došlo je do greške!', {variant: 'error'});
            }
        }
    };

    const onLocationChosen = (e, location) => {
        setSessionSelectedLocation(location);
        setOpenLocationDialog(false);
        enqueueSnackbar("Uspešno dodeljena lokacija", {variant: "success"});
    };

    const filteredData = useMemo(() =>
            (trips ?? [])
                .filter(c => c.canceled !== true)
                .slice(page === 1 ? 0 : MAX_ROWS, page === 1 ? MAX_ROWS : (MAX_ROWS * 2)),
        [trips, page]);

    const issuingItemsPrice = useMemo(() =>
            services.filter(item => item.includeOnIssuingTicket)
                .reduce((acc, item) => acc + item.price, 0),
        [services]);

    if (loading || loadingUserSettings) {
        return <LoadingScreen/>;
    }

    return (
        <Stack sx={{p: 1}}>
            <Typography
                sx={{
                    userSelect: 'none',
                    cursor: 'pointer',
                }}
                onClick={handleLogout}
                variant="h3"
                textAlign="center">
                Polasci u narednom periodu
                za {`${currentDateTime.format('DD.MM.YYYY')}
                vreme: ${currentDateTime.format('HH:mm:ss')} (${page}/2)`}
            </Typography>
            {filteredData.map((trip, index) =>
                <DepartureCard
                    index={index}
                    key={`${trip.tripId}-${index}`}
                    issuingItemsPrice={issuingItemsPrice}
                    trip={trip}/>
            )}
            {(trips?.length === 0 && !loading) &&
                <EmptyContent
                    title={translate("noTripsOnDate")}
                    sx={{
                        '& span.MuiBox-root': {height: 160},
                    }}
                />
            }
            {openLocationDialog && <LocationDialog
                locations={locations}
                loading={loadingLocation}
                open={openLocationDialog}
                error={error}
                handleLocationClick={onLocationChosen}/>}
        </Stack>
    );
}
